import {
	MenuLinks,
	MenuTitle,
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuLink,
	NavigationMenuList,
	NavigationMenuTrigger,
	NavigationMenuTriggerLink,
} from "@/components/ui/NavigationMenu";
import type {
	BlokNavigationSection,
	BlokLink,
	BlokCategorySection,
	BlokSubMenu,
} from "@/storyblok";
import { sbLink } from "@/lib/storyblok/link";
import { StoryblokComponentList } from "@frend-digital/storyblok";
import { clsx } from "@frend-digital/ui";

import type { ReactNode } from "react";
import styles from "./Header.module.css";
import { Link } from "@/i18n/Link";
import type { StoryblokMultilink } from "storyblok";
import { SegmentControl } from "@/components/SegmentControl";

function adjustArrays<T, U>(array1: T[], array2: U[]): U[] {
	// If array1 length is <= 3, return array2 unchanged
	if (array1.length <= 3) {
		return [...array2];
	}

	// Calculate how many elements to keep in array2
	// For array1.length = 4, keep 2 elements
	// For array1.length = 5, keep 1 element
	// For array1.length >= 6, keep 0 elements
	const elementsToKeep = Math.max(0, 2 - (array1.length - 2));

	// Return a new array with only the elements to keep
	return [...array2].slice(0, elementsToKeep);
}

export const MenuList = ({
	sections,
	children,
	className,
	preview,
}: {
	sections: (BlokNavigationSection | BlokLink)[];
	children?: ReactNode;
	className?: string;
	preview: boolean;
}) => {
	return (
		<NavigationMenuList className={className}>
			{sections.map((section) => {
				if (section.component === "link") {
					const link = sbLink(section.link as StoryblokMultilink);
					const trigger = link ? <Link href={link.href}>{section.text}</Link> : section.text;
					return (
						<SegmentControl key={section._uid} segments={section}>
							<NavigationMenuItem>
								<NavigationMenuTriggerLink asChild={typeof trigger !== "string"}>
									{trigger}
								</NavigationMenuTriggerLink>
							</NavigationMenuItem>
						</SegmentControl>
					);
				}

				const link = sbLink(section.link as StoryblokMultilink);
				const trigger = link ? <Link href={link.href}>{section.title}</Link> : section.title;

				return (
					<NavigationMenuItem key={section._uid}>
						<NavigationMenuTrigger asChild={typeof trigger !== "string"}>
							{trigger}
						</NavigationMenuTrigger>
						<NavigationMenuContent>
							<div
								className={clsx(styles.grid, section.equalContentCards && styles.equalContentCards)}
							>
								<MenuListSection section={section} />
								{(() => {
									if (!section.sections || !section.content) return null;

									const adjustedSections = adjustArrays(
										section.sections,
										section.content.slice(0, 2),
									);
									return (
										<StoryblokComponentList
											className={styles.contentCard}
											bloks={adjustedSections}
											preview={preview}
										/>
									);
								})()}
							</div>
						</NavigationMenuContent>
					</NavigationMenuItem>
				);
			})}
			{children}
		</NavigationMenuList>
	);
};

const MenuListSection = ({
	section: data,
	level = 0,
}: {
	section: BlokSubMenu | BlokNavigationSection | BlokLink | BlokCategorySection;
	level?: number;
}) => {
	if (data.component === "subMenu") {
		if (level > 1) {
			const link = sbLink(data.link as StoryblokMultilink);

			if (!link?.href) return null;

			return <NavigationMenuLink href={link.href}>{data.title}</NavigationMenuLink>;
		}

		return (
			<div className={styles.section}>
				<MenuTitle>{data.title}</MenuTitle>
				<MenuLinks>
					{data.items?.map((section) => {
						return (
							<li key={section._uid}>
								<MenuListSection level={level + 1} section={section} />
							</li>
						);
					})}
				</MenuLinks>
			</div>
		);
	}

	if (data.component === "navigationSection") {
		return (
			<>
				{data.sections?.map((section) => {
					return <MenuListSection level={level + 1} key={section._uid} section={section} />;
				})}
			</>
		);
	}

	if (data.component === "categorySection") {
		return null;
	}

	const link = sbLink(data.link as StoryblokMultilink);
	if (!link) return null;

	return <NavigationMenuLink href={link.href}>{data.text}</NavigationMenuLink>;
};
